/**
 * Privacy policy for the early-access release. NOT LEGAL ADVICE.
 *
 * No lawyer has reviewed this. It ships as a draft because the Index is an
 * early-access proof of concept, and a short policy that is true beats a long one
 * that is aspirational. The governing rule: every sentence must be true of the
 * code as it stands. Where a fact could not be established, the claim was
 * *removed* rather than guessed, which is why this document says nothing about
 * log-retention windows, data processing agreements, or international-transfer
 * safeguards. Saying less is how it stays honest.
 *
 * The two hosting regions ARE stated, because both were read from the live
 * providers rather than assumed: Supabase reports `eu-central-1`, and Railway's
 * deploy manifest pins both services to `europe-west4-drams3a`. Neither region
 * is pinned in this repo (`railway.toml` sets none) so a dashboard change can
 * move them without touching code. Re-read them before trusting section 6.
 *
 * The corporate identity facts are NOT invented and NOT taken from the code:
 *  - Controller, group entities, and the data-protection contact point come from
 *    Board of Innovation's published group privacy policy (linked from the
 *    boardofinnovation.com footer), which names MATTERHOOD NV as controller,
 *    "hereafter: Board of Innovation".
 *  - The registered address comes from the Belgian Crossroads Bank for
 *    Enterprises (KBO/BCE), which as of 7 October 2025 records
 *    "Regine Beerplein 1 box 202" for Matterhood NV (0685.577.588) AND for
 *    Board of Innovation BV (0818.009.314). The published group policy still
 *    says "bus 201"; the register is authoritative, so box 202 is used here.
 *
 * Claims that are load-bearing and code-coupled, i.e. rewrite this policy if any
 * of them stops being true:
 *  - No third-party AI or LLM provider processes any user content. The prefill
 *    analyzer (`backend/.../prefill/analyze.py`) is a keyword heuristic, and no
 *    AI-provider SDK is a backend dependency. See "A note on AI" below. Revise
 *    this policy and obtain a DPA before any LLM integration ships.
 *  - Product analytics are FIRST-PARTY ONLY: a `public.analytics_events` table
 *    holding a random, in-memory session id (never a cookie or browser storage),
 *    an event name, and an API-enforced coarse payload allow-list: no user id,
 *    no IP address, nothing linking an event to a person. Events are deleted 90
 *    days after they are written by the pg_cron job
 *    `ai_disruption_exposure_index_analytics_retention`. Sections 2.5, 3 and 8
 *    depend on ALL of these staying true. Introducing ANY third-party analytics
 *    SDK (rather than this first-party table) falsifies sections 5, 6 and 7 and
 *    makes a consent banner a live question again: update this policy in the
 *    same pull request, not afterwards.
 *  - The webfont is self-hosted (`@fontsource/dm-sans`, imported in `main.tsx`)
 *    and `index.html` loads NO third-party resource. If a font, script, or pixel
 *    is ever served from someone else's domain again, sections 2.5, 5, 6 and 7
 *    all become wrong and a consent banner becomes a live question again.
 *  - Sub-processors are exactly: Supabase, Railway, and Resend.
 *  - Report-invite emails are sent exclusively through Resend by the backend
 *    `email_delivery.py`, and only when `RESEND_API_KEY`, `EMAIL_FROM` and
 *    `FRONTEND_URL` are all configured. The email contains the invitee address,
 *    the inviter's name/email, the report label, and the invitation link. If
 *    Resend is ever swapped, or another email leaves the app through a different
 *    provider, sections 5, 6 and 8 are wrong.
 *  - Section 6's Resend region claim is dashboard state, not code: the send API
 *    is region-agnostic, and the claim holds only while the verified sending
 *    domain sits in Resend's `eu-west-1` region. The DPF certification, the
 *    SCC-bearing DPA, and section 8's 30-day log retention were verified
 *    2026-07-13 against Resend's published DPA and changelog. Re-verify all
 *    four whenever the Resend account or sending domain is (re)configured.
 *  - Google is the ONLY single sign-on provider the software can start: it is
 *    staff-facing, offered on `/login` only, and only while it is enabled for the
 *    organisation in Supabase. This matches `AuthProvider = Literal["google"]` in
 *    the backend (Microsoft/azure was removed) and `OAuthButtons` rendering
 *    solely on the `/login` page; section 2.1 describes exactly this. Revise 2.1
 *    if another provider is ever added, if the button spreads beyond `/login`, or
 *    if the "Google shares your name and email" claim stops matching what the
 *    OAuth session exchange actually reads.
 *  - Deletion is self-service, and deleting an account ERASES the assessments it
 *    owns rather than detaching them. Sections 8 and 9 depend on
 *    `DELETE /assessments/{id}`, `POST /auth/delete-account`, and the erase step
 *    in the admin user-delete path all staying in place. If any reverts to
 *    detaching, or the self-service routes are removed, both sections are wrong.
 *  - A scheduled job (`public.delete_old_assessments`, run by pg_cron) deletes
 *    each assessment 1825 days (five years) after it was created. Section 8's
 *    retention table depends on that job staying scheduled and on that interval;
 *    change either and the table is wrong.
 *
 * Beware the absolute negative that rests on infrastructure rather than on
 * application code: it is where this policy has already been wrong once. Two
 * live examples, both disclosed rather than denied:
 *  - IP addresses reach *logs* even though no code path writes them to the
 *    database. `frontend/nginx.conf.template` sets no `access_log off` and
 *    `backend/Dockerfile` runs uvicorn without `--no-access-log`, so both
 *    default to logging the client IP, the request path, and the user-agent.
 *  - Prefill uploads are not held purely "in memory": Starlette's multipart
 *    parser spools any part over 1 MiB to a temporary file on disk
 *    (`starlette/formparsers.py`, `spool_max_size`). It is deleted when the
 *    request ends, so "we do not keep it" holds; "in memory" did not.
 * Before writing "never", "no ...", or "not stored", check the web server, the
 * app server, and the framework, not just this repo's own source.
 */
export const PRIVACY_POLICY_MARKDOWN = `
## 1. Who we are

The **AI Disruption Exposure Index** ("the Index", "this tool") is a diagnostic
operated by Board of Innovation. It asks about your organisation and returns a
report estimating how exposed your business and operating models are to
disruption from AI.

Board of Innovation is the **data controller** for the personal data described
in this policy. That means we decide why and how it is processed.

- **Controller:** Matterhood NV, trading as Board of Innovation, registered in the register of legal entities (Antwerp, division Antwerp) under number 0685.577.588
- **Registered address:** Regine Beerplein 1 box 202, 2018 Antwerp, Belgium
- **Privacy contact:** hello@boardofinnovation.com
- **Data Protection Officer:** Board of Innovation maintains a data-protection contact point rather than a designated Data Protection Officer. Use the privacy contact above.

## 2. What we collect

### 2.1 Account details

To create an account we collect your **email address**, your **first and last
name**, and a **password**. The password is handled by our authentication
provider and stored only in hashed form. Board of Innovation never sees or
stores your password.

Email and password is the default way to sign in. The software supports exactly
one single sign-on provider, **Google**, offered to Board of Innovation staff
on the sign-in page, and only while it is enabled for our organisation. If you
sign in with Google, Google confirms your identity and shares your name and email
address with us; we never receive or store a password for such an account. No
other sign-in method exists.

### 2.2 Assessment responses

When you run an assessment we collect what you tell us about the organisation
you are assessing:

- company name and website address
- sector and sub-sector
- geography
- company size
- business-model type
- the job role of the person answering
- an optional free-text field for extra context
- your answers to a fixed set of multiple-choice questions (currently 42), each
  recorded as a number from 0 to 100

We also store the report we generate from those answers, and, where a Board of
Innovation expert has reviewed the assessment, the expert's calibration notes
and adjusted scores.

### 2.3 Documents you supply to pre-fill an assessment

You may optionally upload a document, paste text, or give us a URL so the tool
can suggest answers for you. **We do not keep the document, the pasted text, or
the fetched page.** It is processed only while we are handling your request and
is then discarded; a larger upload may be buffered to a temporary file on the
server for the lifetime of that request. Only the short derived summary and the
suggested answers are stored with your assessment.

Do not upload anything you are not entitled to share with us. If a document
contains personal data about other people, you are responsible for having a
lawful basis to disclose it.

### 2.4 Activity logs

We record administrative and security-relevant events: who signed in, who
opened or shared a report, who changed a user's role. Each record holds a user
identifier, the action, a short description, and a timestamp. That description
sometimes contains an email address.

### 2.5 What we do **not** collect

- **No third-party analytics, no tracking cookies, no profiling.** We record a
  small set of first-party product-usage events (for example "assessment
  started" or "report shared") under a random session identifier that exists
  only in your browser tab's memory for the duration of your visit. It is never
  written to a cookie or to your browser's storage, and it is never linked to
  your name, email address, IP address, or account. The Index carries no
  analytics SDK, no advertising pixel, and no third-party tracker.
- **No third-party fonts, scripts, or pixels.** Every asset the page loads,
  including the typeface, is served from our own servers, so opening a page
  does not disclose your IP address to anyone but us.
- **No IP addresses in our database.** We do not write your IP address to our
  database, and the rate limiter that protects our public endpoints holds it in
  memory only. It does appear in our server logs; see below.
- **No special-category data.** Please do not enter health, biometric, political,
  religious, or similarly sensitive personal data into the free-text field.
- **We never sell your personal data**, and we do not use it for advertising.

**About server logs.** Like essentially every web service, our web server and our
application server write one standard access-log line for each request. That line
records the IP address the request came from, the page or API path that was
requested, and your browser's user-agent. These logs are held by our hosting
provider. We use them to keep the service running and to investigate abuse. We do
not use them for analytics, and we do not use them to profile you.

## 3. Why we process it, and on what legal basis

| Data | Why we process it | Lawful basis |
| --- | --- | --- |
| Account details | Create your account, authenticate you, and let you return to your reports | Performance of a contract |
| Assessment responses | Generate your report and let you reopen it later | Performance of a contract |
| Optional free-text context | Make the generated report more relevant to your organisation | Performance of a contract |
| Expert calibration notes | Deliver the expert-reviewed version of your report | Performance of a contract |
| Activity logs | Keep the service secure, investigate abuse, and support administration | Legitimate interests |
| A colleague's email address, when you invite them to a report | Give the person you named access to that report | Legitimate interests |
| IP address | Rate-limit requests to prevent abuse, and (via the server access logs described in section 2.5) keep the service running and investigate abuse | Legitimate interests |
| Product-usage events (a random session identifier, an event name such as "assessment started", and at most one coarse detail such as the step name or the sharing method) | Understand where the assessment loses people so we can improve it | Legitimate interests |

We do **not** use your assessment responses to train or tune any model, to
build a benchmark, or for any purpose other than producing and serving your own
report.

## 4. A note on AI

Despite the subject matter, **this tool does not send your responses to any
third-party AI provider.** Your report is produced by a deterministic,
rule-based scoring engine that runs on our own servers. The optional pre-fill
feature that suggests answers from a document is a plain keyword heuristic, not
a language model.

We intend to replace that heuristic with a large language model in a future
release. **Before any such integration ships**, this policy must be revised to
name the provider, a data processing agreement must be in place with them, and
any consent their involvement requires must be obtained. Until you see that
change reflected here, no AI provider receives your data.

## 5. Who we share it with

We do not sell your data and we do not share it with advertisers. We rely on three
**sub-processors** to run the service:

| Sub-processor | What it does | What it can access |
| --- | --- | --- |
| Supabase | Authentication, our database, and the account emails we send you (sign-in verification and similar) | Account details, assessments, reports, activity logs, and the anonymous product-usage events described in section 2.5 |
| Railway | Hosting for the application | Data in transit through the running application, and the server access logs described in section 2.5, which contain visitor IP addresses |
| Resend | Delivers the report-invitation email when you invite a colleague to a report, where email delivery is enabled | The colleague's email address, the inviting user's name and email address, the report title, and the invitation link |

**Within the Board of Innovation group.** Board of Innovation's published policy
names three group entities that personal data may be shared with: Board of
Innovation BV (Antwerp, Belgium), Board of Innovation APAC Pte. Ltd. (Singapore),
and Board of Innovation US Inc. (New York, USA).

Be aware that the expert-calibration feature gives Board of Innovation staff who
hold an expert or administrator role **read access to every user's report**, and
such a person may work for any of those entities.

Beyond these, we disclose personal data only where the law requires it, or where
it is necessary to establish, exercise, or defend a legal claim.

**When you share a report yourself.** You control two ways of showing a report to
someone else, and both are off unless you turn them on:

- A **public view-only link**. Anyone holding the link can read the report
  without an account. The published version is stripped of your account details
  and of any expert-calibration notes. Turning the link off revokes it
  permanently. Treat the link as public, because we cannot control who it is
  forwarded to.
- An **invitation to a named colleague**. We store the email address you give us
  so that person, and only that person, can open the report once they sign in.
  You can revoke the invitation. If email delivery is enabled, we also send the
  invitation link to that address for you through our email provider, Resend; you
  can always copy the link and share it through a channel you choose instead.

## 6. International transfers

- **Supabase**, which holds your account and your assessments, runs in the
  \`eu-central-1\` region: Frankfurt, Germany, inside the EEA.
- **Railway**, which hosts the application, deploys it in its
  \`europe-west4\` region: Amsterdam, the Netherlands, inside the EEA. Railway
  itself is a United States company, so data passing through the running
  application, and the server access logs described in section 2.5, are
  processed by a US provider even though the servers stand in the EEA.
- **Resend**, which delivers our report-invitation emails, is a United States
  company. Our sending domain is registered in Resend's \`eu-west-1\` region
  (Ireland, inside the EEA), so the emails themselves are dispatched from EEA
  infrastructure, but Resend stores its account data and sending logs in the
  United States. Resend is certified under the EU-US Data Privacy Framework and
  its data-processing agreement incorporates the EU Standard Contractual
  Clauses.
- **Board of Innovation US Inc.** (New York) and **Board of Innovation APAC Pte. Ltd.** (Singapore) are group entities outside the EEA. Board of Innovation's published policy states that where personal data is transferred from the EEA to a third country, "supplementary measures are in place to guarantee an essentially equivalent level of protection as within the EEA".

## 7. Cookies and local storage

**We set exactly one cookie, and it is not a tracking cookie.**

| Name | Purpose | Properties |
| --- | --- | --- |
| \`boi-auth-session\` | Keeps you signed in | Strictly necessary. HTTP-only, SameSite=Lax, and Secure in production. It holds your email address, your role, and an expiry, authenticated with a signature. It expires after 24 hours, or after 30 days if you tick "remember me". |

The cookie's contents are **signed but not encrypted**: the signature stops
anyone tampering with it, but a person with access to your browser could read
the email address inside it.

We also use your browser's **local storage** to keep an assessment you have
started but not yet submitted, and to remember where to send you back to after
signing in. This never leaves your browser, and clearing your browser data
removes it.

Because the site loads nothing from anyone else's servers (no third-party
fonts, scripts, or pixels), opening a page discloses nothing about you to any
third party, and there is no consent banner to click.

## 8. How long we keep it

| Data | Retention |
| --- | --- |
| Account details | For as long as your account exists |
| Assessments, reports, and expert calibrations | Until you delete them yourself, and at the latest five years after they were created. A nightly job removes anything older |
| Activity logs | Automatically deleted 90 days after they are written |
| Product-usage events | Automatically deleted 90 days after they are written |
| Documents supplied for pre-fill | Not stored. Discarded as soon as the request that carried them finishes |
| IP addresses | Not stored in our database. Recorded in the server access logs described in section 2.5 |
| Report-invitation emails | Delivered by Resend, which retains delivery logs (including the recipient address and message content) for 30 days |

**Our nightly deletion jobs, and exactly what they do.** Every night we delete
assessments (together with their reports and expert calibrations) that are more
than five years old, counting from when they were created; activity logs more
than 90 days old; and product-usage events more than 90 days old. Your account
details are kept for as long as your account exists, and are removed when the
account is deleted.

Deleting your account **erases it completely**: the account, its credentials, and
every assessment you own (each one's report, expert calibration, share links,
and invites) are permanently removed, so nothing identifying survives. You can
also delete a single assessment yourself, from its report page or your history.
When a Board of Innovation administrator deletes an account on your behalf, it has
the same erasing effect.

## 9. Your rights

If you are in the EEA or the UK, you have the right to access your personal data,
to have it corrected, to have it erased, to restrict or object to how we process
it, and to receive it in a portable form. Where we rely on your consent, you may
withdraw it at any time. You may also complain to your national data protection
authority.

**Erasure is now self-service; the other rights are exercised by contacting us.**
You can delete a single report yourself, from its own page or your history, and
you can delete your whole account, and everything it owns, from your profile.
There is still no self-service export of your account data: you can download an
individual report, but not a full copy of everything we hold. To exercise your
other rights (access, rectification, restriction, objection, or portability),
write to the privacy contact in section 1. We will respond within one month of
receiving your request, as GDPR Art. 12(3) requires, and will tell you if we need
to extend that period by up to two further months.

If you are not satisfied with how we handle your request, you may lodge a
complaint with the supervisory authority in your own country, or with ours:

> **Belgian Data Protection Authority** (Gegevensbeschermingsautoriteit /
> Autorité de protection des données)
> Drukpersstraat 35, 1000 Brussels, Belgium
> contact@apd-gba.be · +32 (0)2 274 48 00

### Automated decision-making

Your exposure score is calculated by a fixed, rule-based formula, and any expert
calibration is performed by a person. We do not make decisions producing legal
or similarly significant effects about you by automated means, and the report is
a diagnostic about your organisation rather than a decision about you.

## 10. Changes to this policy

We will update this policy when what we do with your data changes, in
particular before any AI provider is introduced, as described in section 4. When
we make a material change we will say so on this page.

- **Last updated:** 13 July 2026

## 11. Contact us

Questions about this policy, or about the personal data we hold on you, go to
**hello@boardofinnovation.com**, or by post to Matterhood NV, Regine Beerplein 1
box 202, 2018 Antwerp, Belgium.
`.trim();
